/**
 * Form import/export — Supabase orchestration.
 *
 * Export: read groups/forms/definitions (draft or published) and assemble a
 * versioned `FormExportEnvelope` (see ./form-io-core).
 * Import: recreate groups/forms in a target project, write each definition as a
 * draft, and optionally publish v1.0 via the publish edge function.
 *
 * Pure helpers (validation, key de-dup, planning) live in ./form-io-core so the
 * test suite can exercise them without the Supabase client.
 */

import { supabase } from '@/lib/supabase/client'
import { parseDefinition, saveFormDraft, publishFormVersion } from './form-builder-api'
import type { FormDefinition, LocalizedString } from './form-builder-types'
import {
  EXPORT_SCHEMA_VERSION,
  dedupeFormKey,
  type ExportScope,
  type ExportSource,
  type ExportedForm,
  type ExportedGroup,
  type FormExportEnvelope,
  type DefinitionSource,
} from './form-io-core'

// ─── Internal row helpers ────────────────────────────────────────────────────

interface FormRow {
  id: string
  form_key: string
  group_id: string
  title: LocalizedString | null
  description: LocalizedString | null
  icon: string | null
  instance_type: 'single' | 'repeatable'
  required_for_final_submit: boolean
  minimum_instances: number
  instance_label_field_key: string | null
  display_order: number
  current_version_id: string | null
}

interface GroupRow {
  id: string
  title: LocalizedString | null
  description: LocalizedString | null
  icon: string | null
  display_order: number
}

function asLocalized(v: unknown): LocalizedString {
  if (v && typeof v === 'object' && 'fa' in (v as Record<string, unknown>)) {
    return v as LocalizedString
  }
  if (typeof v === 'string') return { fa: v }
  return { fa: '' }
}

function asLocalizedOrNull(v: unknown): LocalizedString | null {
  if (v == null) return null
  return asLocalized(v)
}

// ─── Export ──────────────────────────────────────────────────────────────────

export interface ExportRequest {
  projectId: string
  scope: ExportScope
  source: ExportSource
  /** Required when scope === 'group'. */
  groupId?: string
  /** Required when scope === 'form'. */
  formId?: string
}

/**
 * Resolve the definition for one form according to the requested source,
 * falling back to the other source when the requested one is absent. Returns
 * the normalized definition plus an honest `definition_source` marker.
 */
function resolveDefinition(
  source: ExportSource,
  draftDef: unknown | null,
  publishedDef: unknown | null,
  publishedLabel: string | null,
): { definition: FormDefinition; definition_source: DefinitionSource; source_version_label: string | null } {
  const order: DefinitionSource[] = source === 'draft' ? ['draft', 'published'] : ['published', 'draft']
  for (const s of order) {
    if (s === 'draft' && draftDef != null) {
      return { definition: parseDefinition(draftDef), definition_source: 'draft', source_version_label: null }
    }
    if (s === 'published' && publishedDef != null) {
      return {
        definition: parseDefinition(publishedDef),
        definition_source: 'published',
        source_version_label: publishedLabel,
      }
    }
  }
  return {
    definition: { fields: [], conditional_rules: [], calculated_fields: [] },
    definition_source: 'empty',
    source_version_label: null,
  }
}

export async function fetchExport(req: ExportRequest): Promise<FormExportEnvelope> {
  const { projectId, scope, source } = req

  // Project (for envelope metadata + filename)
  const { data: project, error: projErr } = await supabase
    .from('projects')
    .select('id, name')
    .eq('id', projectId)
    .single()
  if (projErr) throw projErr

  // Resolve target group + form rows based on scope.
  let groupRows: GroupRow[] = []
  let formRows: FormRow[] = []

  const formCols =
    'id, form_key, group_id, title, description, icon, instance_type, required_for_final_submit, minimum_instances, instance_label_field_key, display_order, current_version_id'
  const groupCols = 'id, title, description, icon, display_order'

  if (scope === 'project') {
    const [g, f] = await Promise.all([
      supabase.from('form_groups').select(groupCols).eq('project_id', projectId).eq('is_deleted', false).order('display_order'),
      supabase.from('forms').select(formCols).eq('project_id', projectId).eq('is_deleted', false).order('display_order'),
    ])
    if (g.error) throw g.error
    if (f.error) throw f.error
    groupRows = (g.data ?? []) as GroupRow[]
    formRows = (f.data ?? []) as FormRow[]
  } else if (scope === 'group') {
    if (!req.groupId) throw new Error('groupId is required for group export')
    const [g, f] = await Promise.all([
      supabase.from('form_groups').select(groupCols).eq('id', req.groupId).single(),
      supabase.from('forms').select(formCols).eq('group_id', req.groupId).eq('is_deleted', false).order('display_order'),
    ])
    if (g.error) throw g.error
    if (f.error) throw f.error
    groupRows = [g.data as GroupRow]
    formRows = (f.data ?? []) as FormRow[]
  } else {
    // scope === 'form'
    if (!req.formId) throw new Error('formId is required for form export')
    const { data: form, error: fErr } = await supabase.from('forms').select(formCols).eq('id', req.formId).single()
    if (fErr) throw fErr
    formRows = [form as FormRow]
    const { data: grp, error: gErr } = await supabase.from('form_groups').select(groupCols).eq('id', (form as FormRow).group_id).single()
    if (gErr) throw gErr
    groupRows = [grp as GroupRow]
  }

  // Batch-fetch drafts + published snapshots for all forms in one round-trip each.
  const formIds = formRows.map((f) => f.id)
  const versionIds = formRows.map((f) => f.current_version_id).filter((v): v is string => !!v)

  const draftsByForm = new Map<string, unknown>()
  const versionById = new Map<string, { def: unknown; label: string }>()

  if (formIds.length) {
    const { data: drafts, error: dErr } = await supabase
      .from('form_drafts')
      .select('form_id, draft_definition')
      .in('form_id', formIds)
    if (dErr) throw dErr
    for (const d of drafts ?? []) draftsByForm.set(d.form_id, d.draft_definition)
  }
  if (versionIds.length) {
    const { data: versions, error: vErr } = await supabase
      .from('form_versions')
      .select('id, definition_snapshot, version_major, version_minor')
      .in('id', versionIds)
    if (vErr) throw vErr
    for (const v of versions ?? []) {
      versionById.set(v.id, { def: v.definition_snapshot, label: `${v.version_major}.${v.version_minor}` })
    }
  }

  // Assemble grouped forms.
  const exportedFormFor = (f: FormRow): ExportedForm => {
    const pub = f.current_version_id ? versionById.get(f.current_version_id) : undefined
    const resolved = resolveDefinition(
      source,
      draftsByForm.has(f.id) ? draftsByForm.get(f.id) : null,
      pub?.def ?? null,
      pub?.label ?? null,
    )
    return {
      form_key: f.form_key,
      title: asLocalized(f.title),
      description: asLocalizedOrNull(f.description),
      icon: f.icon,
      instance_type: f.instance_type,
      required_for_final_submit: f.required_for_final_submit,
      minimum_instances: f.minimum_instances ?? 0,
      instance_label_field_key: f.instance_label_field_key,
      display_order: f.display_order,
      ...resolved,
    }
  }

  const groups: ExportedGroup[] = groupRows.map((g) => ({
    title: asLocalized(g.title),
    description: asLocalizedOrNull(g.description),
    icon: g.icon,
    display_order: g.display_order,
    forms: formRows.filter((f) => f.group_id === g.id).map(exportedFormFor),
  }))

  return {
    bluedatalake_form_export: EXPORT_SCHEMA_VERSION,
    exported_at: new Date().toISOString(),
    scope,
    requested_source: source,
    project: { id: project.id, title: asLocalizedOrNull(project.name) },
    groups,
  }
}

// ─── Import ──────────────────────────────────────────────────────────────────

export type PublishMode = 'draft' | 'publish'

export interface ImportRequest {
  envelope: FormExportEnvelope
  targetProjectId: string
  userId: string
  /** When set, every imported form is placed in this existing group and no new
   *  groups are created. When omitted, each envelope group is recreated. */
  targetGroupId?: string
  publishMode: PublishMode
}

export interface ImportFormResult {
  originalKey: string
  finalKey: string
  renamed: boolean
  published: boolean
  /** Set when this specific form failed; the rest of the import still proceeds. */
  error?: string
}

export interface ImportResult {
  groupsCreated: number
  formsCreated: number
  formsPublished: number
  formsRenamed: number
  forms: ImportFormResult[]
  /** Forms that failed to create at all. */
  failures: Array<{ formKey: string; error: string }>
}

async function nextGroupDisplayOrder(projectId: string): Promise<number> {
  const { data } = await supabase
    .from('form_groups')
    .select('display_order')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .order('display_order', { ascending: false })
    .limit(1)
    .maybeSingle()
  return (data?.display_order ?? -1) + 1
}

async function nextFormDisplayOrder(groupId: string): Promise<number> {
  const { data } = await supabase
    .from('forms')
    .select('display_order')
    .eq('group_id', groupId)
    .eq('is_deleted', false)
    .order('display_order', { ascending: false })
    .limit(1)
    .maybeSingle()
  return (data?.display_order ?? -1) + 1
}

async function createGroup(
  projectId: string,
  userId: string,
  g: ExportedGroup,
  displayOrder: number,
): Promise<string> {
  const { data, error } = await supabase
    .from('form_groups')
    .insert({
      project_id: projectId,
      created_by: userId,
      title: g.title,
      description: g.description,
      icon: g.icon ?? 'folder',
      display_order: displayOrder,
    })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

async function createImportedForm(
  projectId: string,
  groupId: string,
  userId: string,
  f: ExportedForm,
  formKey: string,
  displayOrder: number,
): Promise<string> {
  const { data, error } = await supabase
    .from('forms')
    .insert({
      project_id: projectId,
      group_id: groupId,
      created_by: userId,
      form_key: formKey,
      title: f.title,
      description: f.description,
      icon: f.icon ?? 'file-text',
      instance_type: f.instance_type,
      required_for_final_submit: f.required_for_final_submit,
      minimum_instances: f.minimum_instances ?? 0,
      instance_label_field_key: f.instance_label_field_key,
      display_order: displayOrder,
      is_published: false,
    })
    .select('id')
    .single()
  if (error) throw error
  return data.id
}

export async function runImport(req: ImportRequest): Promise<ImportResult> {
  const { envelope, targetProjectId, userId, targetGroupId, publishMode } = req

  // Seed the de-dup set with form_keys already in the target project.
  const { data: existing, error: exErr } = await supabase
    .from('forms')
    .select('form_key')
    .eq('project_id', targetProjectId)
    .eq('is_deleted', false)
  if (exErr) throw exErr
  const takenKeys = new Set((existing ?? []).map((r) => r.form_key))

  const result: ImportResult = {
    groupsCreated: 0,
    formsCreated: 0,
    formsPublished: 0,
    formsRenamed: 0,
    forms: [],
    failures: [],
  }

  let groupOrder = targetGroupId ? 0 : await nextGroupDisplayOrder(targetProjectId)

  for (const g of envelope.groups) {
    // Resolve the destination group for this envelope group.
    let destGroupId: string
    if (targetGroupId) {
      destGroupId = targetGroupId
    } else {
      try {
        destGroupId = await createGroup(targetProjectId, userId, g, groupOrder++)
        result.groupsCreated++
      } catch (e) {
        // Whole group failed → record each of its forms as a failure and skip.
        for (const f of g.forms) {
          result.failures.push({ formKey: f.form_key, error: (e as Error).message })
        }
        continue
      }
    }

    // Fresh groups start at 0; an existing target group continues after its
    // current forms so display_order stays monotonic.
    let formOrder = targetGroupId ? await nextFormDisplayOrder(destGroupId) : 0
    for (const f of g.forms) {
      const finalKey = dedupeFormKey(f.form_key, takenKeys)
      takenKeys.add(finalKey)
      const renamed = finalKey !== f.form_key

      try {
        const formId = await createImportedForm(targetProjectId, destGroupId, userId, f, finalKey, formOrder++)

        // Normalize defensively (handles legacy/hand-edited envelopes).
        const definition = parseDefinition(f.definition)
        const draft = await saveFormDraft(formId, userId, definition)

        let published = false
        const hasFields = definition.fields.length > 0
        if (publishMode === 'publish' && hasFields && f.definition_source !== 'empty') {
          await publishFormVersion({
            form_id: formId,
            draft_version: draft.version,
            client_diff: [],
            decisions: [],
          })
          published = true
          result.formsPublished++
        }

        result.formsCreated++
        if (renamed) result.formsRenamed++
        result.forms.push({ originalKey: f.form_key, finalKey, renamed, published })
      } catch (e) {
        result.failures.push({ formKey: f.form_key, error: (e as Error).message })
        result.forms.push({
          originalKey: f.form_key,
          finalKey,
          renamed,
          published: false,
          error: (e as Error).message,
        })
      }
    }
  }

  return result
}

/**
 * Fetch the form_keys already present in a project — used by the import dialog
 * to preview which keys will be renamed (via core `planImport`).
 */
export async function fetchProjectFormKeys(projectId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('forms')
    .select('form_key')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
  if (error) throw error
  return (data ?? []).map((r) => r.form_key)
}
